/**
 * WAV (RIFF) encoder. Pure functions — unit-testable.
 *
 * Formats follow professional delivery practice:
 *   - 24-bit PCM (default) — the standard deliverable. Its noise floor sits far
 *     below any playback system, so no dither is required.
 *   - 32-bit IEEE float   — bit-exact with the Web Audio render, and samples
 *     above 0 dBFS survive. Used for stems/transfer where nothing may be lost.
 *   - 16-bit PCM          — CD/compatibility. Reducing to 16 bits DOES need
 *     dither, so TPDF is applied by default at this depth only.
 *
 * Samples are rounded, never truncated. Truncation biases every sample toward
 * zero, which produces correlated distortion rather than benign noise — audible
 * on reverb tails and fades, and the reason no professional encoder truncates.
 */

export const SUPPORTED_BIT_DEPTHS = [16, 24, 32];
export const DEFAULT_BIT_DEPTH = 24;

export function interleaveChannels(channels) {
  if (!Array.isArray(channels) || channels.length === 0) return new Float32Array(0);
  const numChannels = channels.length;
  const length = Math.min(...channels.map((c) => c?.length || 0));
  const out = new Float32Array(length * numChannels);
  for (let i = 0; i < length; i += 1) {
    for (let ch = 0; ch < numChannels; ch += 1) {
      out[(i * numChannels) + ch] = channels[ch][i] || 0;
    }
  }
  return out;
}

/** Concatenate a list of Float32Array chunks into one array. */
export function concatChunks(chunks) {
  const total = chunks.reduce((sum, c) => sum + (c?.length || 0), 0);
  const out = new Float32Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    if (!chunk?.length) return;
    out.set(chunk, offset);
    offset += chunk.length;
  });
  return out;
}

const utf8ToBytes = (text) => {
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(text);
  const out = [];
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    else out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
  }
  return new Uint8Array(out);
};

const INFO_TAG_FIELDS = [
  ['INAM', 'title'], ['IART', 'artist'], ['IPRD', 'album'],
  ['ICMT', 'comment'], ['ICRD', 'year'], ['IGNR', 'genre']
];

/** RIFF LIST/INFO chunk bytes for the given metadata, or null when empty. */
export function buildInfoChunk(metadata = {}) {
  const tags = [];
  INFO_TAG_FIELDS.forEach(([id, field]) => {
    const text = String(metadata?.[field] ?? '').trim();
    if (text) tags.push({ id, bytes: utf8ToBytes(text.slice(0, 512)) });
  });
  if (metadata?.software !== null) {
    tags.push({ id: 'ISFT', bytes: utf8ToBytes(String(metadata?.software || 'CookUp')) });
  }
  if (tags.length === 0) return null;

  let payloadBytes = 0;
  const encoded = tags.map((tag) => {
    const textLength = tag.bytes.length + 1; // null-terminated C string
    const paddedLength = textLength + (textLength % 2); // chunks are word aligned
    payloadBytes += 8 + paddedLength;
    return { ...tag, textLength, paddedLength };
  });

  const listDataSize = 4 + payloadBytes; // 'INFO' + subchunks
  const buffer = new ArrayBuffer(8 + listDataSize);
  const view = new DataView(buffer);
  const out = new Uint8Array(buffer);
  const writeTag = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  writeTag(0, 'LIST');
  view.setUint32(4, listDataSize, true);
  writeTag(8, 'INFO');
  let offset = 12;
  encoded.forEach((tag) => {
    writeTag(offset, tag.id);
    view.setUint32(offset + 4, tag.textLength, true);
    out.set(tag.bytes, offset + 8);
    out[offset + 8 + tag.bytes.length] = 0;
    offset += 8 + tag.paddedLength;
  });
  return out;
}

/**
 * Triangular (TPDF) dither in LSB units, spanning (-1, 1).
 * The difference of two independent uniforms is triangular — the standard
 * choice for bit-depth reduction because it fully decorrelates the
 * quantization error from the signal at the cost of a fixed, benign noise floor.
 */
const tpdf = (random) => random() - random();

/**
 * Encode interleaved Float32 samples to a WAV ArrayBuffer.
 *
 * @param {Float32Array} interleaved  interleaved sample frames
 * @param {object} options
 *   sampleRate  - output rate (Hz)
 *   numChannels - channel count
 *   bitDepth    - 16 | 24 | 32 (float). Unsupported values fall back to 24
 *                 rather than throwing, so a live take is never lost.
 *   dither      - force TPDF on/off. Default: on for 16-bit, off above it.
 *   metadata    - optional RIFF INFO tags {title, artist, album, comment, year, genre}
 *   random      - injectable RNG, for deterministic tests
 */
export function encodeWav(interleaved, {
  sampleRate,
  numChannels,
  bitDepth = DEFAULT_BIT_DEPTH,
  dither,
  metadata,
  random = Math.random
} = {}) {
  const samples = interleaved || new Float32Array(0);
  const safeRate = Math.max(8000, Math.round(Number(sampleRate) || 44100));
  const safeChannels = Math.max(1, Math.round(Number(numChannels) || 1));
  const depth = SUPPORTED_BIT_DEPTHS.includes(Number(bitDepth)) ? Number(bitDepth) : DEFAULT_BIT_DEPTH;
  const isFloat = depth === 32;
  // Dither only ever helps when reducing to 16 bits. At 24 bits the
  // quantization floor is already below the noise of any playback chain, and
  // float has no quantization step to dither at all.
  const useDither = dither === undefined ? depth === 16 : Boolean(dither) && !isFloat;

  const bytesPerSample = depth / 8;
  const blockAlign = safeChannels * bytesPerSample;
  const dataBytes = samples.length * bytesPerSample;
  const fmtSize = isFloat ? 18 : 16; // float needs the cbSize field
  const factBytes = isFloat ? 12 : 0; // 'fact' chunk is required for non-PCM
  const infoBytes = buildInfoChunk(metadata || {});
  const headerBytes = 12 + (8 + fmtSize) + factBytes + 8;
  const totalBytes = headerBytes + dataBytes + (infoBytes ? infoBytes.length : 0);

  const buffer = new ArrayBuffer(totalBytes);
  const view = new DataView(buffer);
  const writeString = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, totalBytes - 8, true);
  writeString(8, 'WAVE');

  let offset = 12;
  writeString(offset, 'fmt ');
  view.setUint32(offset + 4, fmtSize, true);
  view.setUint16(offset + 8, isFloat ? 3 : 1, true); // 1 = PCM, 3 = IEEE float
  view.setUint16(offset + 10, safeChannels, true);
  view.setUint32(offset + 12, safeRate, true);
  view.setUint32(offset + 16, safeRate * blockAlign, true); // byte rate
  view.setUint16(offset + 20, blockAlign, true);
  view.setUint16(offset + 22, depth, true);
  if (isFloat) view.setUint16(offset + 24, 0, true); // cbSize
  offset += 8 + fmtSize;

  if (isFloat) {
    writeString(offset, 'fact');
    view.setUint32(offset + 4, 4, true);
    view.setUint32(offset + 8, Math.floor(samples.length / safeChannels), true);
    offset += 12;
  }

  writeString(offset, 'data');
  view.setUint32(offset + 4, dataBytes, true);
  offset += 8;

  if (isFloat) {
    // Bit-exact: no clamping, so headroom above 0 dBFS is preserved.
    for (let i = 0; i < samples.length; i += 1) {
      view.setFloat32(offset, samples[i] || 0, true);
      offset += 4;
    }
  } else {
    const scale = 2 ** (depth - 1);
    const maxValue = scale - 1;
    const minValue = -scale;
    for (let i = 0; i < samples.length; i += 1) {
      const scaled = (samples[i] || 0) * scale;
      const dithered = useDither ? scaled + tpdf(random) : scaled;
      let value = Math.round(dithered);
      if (value > maxValue) value = maxValue;
      else if (value < minValue) value = minValue;
      if (depth === 16) {
        view.setInt16(offset, value, true);
        offset += 2;
      } else {
        const unsigned = value < 0 ? value + 0x1000000 : value;
        view.setUint8(offset, unsigned & 0xff);
        view.setUint8(offset + 1, (unsigned >> 8) & 0xff);
        view.setUint8(offset + 2, (unsigned >> 16) & 0xff);
        offset += 3;
      }
    }
  }

  if (infoBytes) new Uint8Array(buffer, offset, infoBytes.length).set(infoBytes);

  return buffer;
}
